import {useQueryClient} from '@tanstack/react-query';
import Translate from '@src/components/Translate/Translate';
import MessageField from '@src/components/Chat/MessageField';
import React, {useCallback, useContext, useState} from 'react';
import {UserContext} from '@src/contexts/UserContext';
import app from '@src/helpers/app';
import {refetchAllOrderQueriesExceptDisabled} from '../refetchAllOrderQueriesExceptDisabled';
import Portal from '@src/components/Portal/Portal';

export default function ExcuseForm(props: {
    portal: React.RefObject<HTMLDivElement>,
    modelId: string | number,
    productId?: string | number,
    sendToRouteName: string,
    controllerName: string,
    modelName?: string,
    alertMessage?: string,
    ignorePermission?: string,
    onIgnore?: (newState: boolean) => void,
    onSpecify?: () => void,
}) {
    const userContext = useContext(UserContext);
    const [messageRequired, setMessageRequired] = useState(false);
    const queryClient = useQueryClient();

    const {portal, controllerName, modelName, modelId, productId, sendToRouteName, onIgnore, onSpecify} = props;
    const displayIgnore = onIgnore && (
            !props.ignorePermission ||
            userContext &&
            userContext.can(props.ignorePermission)
    );

    type OnPostCallback = NonNullable<React.ComponentProps<typeof MessageField>['onPostMessage']>;
    const onPost = useCallback<OnPostCallback>((message, attachments) => {
        setMessageRequired(false);
        if (!message?.length) {
            setMessageRequired(true);

            return Promise.resolve(false);
        }

        return app('http').route(sendToRouteName, {
            id: String(modelId),
            product: String(productId),
        }, {
            headers: {'content-type': 'multipart/form-data'},
            body: {['controller-name']: controllerName, message, attachments}
        }).then(value => {
            refetchAllOrderQueriesExceptDisabled(
                    queryClient,
                    app('queryKeys').getAllQueriesOfExactModelQueryKey(modelName ?? controllerName, modelId)
            ).then();

            if (onSpecify) {
                onSpecify();
            }

            return value;
        });
    }, [sendToRouteName, modelId, productId, controllerName, queryClient, modelName, onSpecify]);

    return <Portal container={portal.current}>
        <div>
            {displayIgnore ? <div className="mb-2">
                <div className="d-flex justify-content-end align-items-center">
                    <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => onIgnore(true)}
                    >
                        <i className="fa fa-times"/>
                    </button>
                </div>
            </div> : null}

            {props.alertMessage ? <div className="alert alert-danger px-3 py-2 mb-2">
                <i className="fa fa-exclamation-triangle me-2"/>

                <Translate message={props.alertMessage}/>
            </div> : null}

            <MessageField onPostMessage={onPost} isInvalid={messageRequired}/>
        </div>
    </Portal>;
}
